import { useState, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import CustomCursor from './components/CustomCursor';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import WorkShowcase from './components/WorkShowcase';
import Capabilities from './components/Capabilities';
import ExperienceTimeline from './components/ExperienceTimeline';
import Store from './components/Store';
import Testimonials from './components/Testimonials';
import ContactForm from './components/ContactForm';
import Preloader from './components/Preloader';

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Check locally for previously saved preferences
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }
      return 'dark'; // Default to a gorgeous dark slate mode
    }
    return 'dark';
  });

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      return next;
    });
  };

  useEffect(() => {
    // Smoothly toggle .dark class across both documentElement and document.body
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (!isLoading) {
      // Force scroll/resize events in window to trigger intersection observers for accurate first-scroll animations
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        window.dispatchEvent(new Event('scroll'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <AnimatePresence mode="wait">
        {isLoading ? (
          <Preloader key="preload" onComplete={() => setIsLoading(false)} />
        ) : (
          <div key="content" className="min-h-screen font-sans transition-all duration-700 bg-transparent text-neutral-900 dark:text-neutral-50 selection:bg-emerald-500 selection:text-white pb-6 relative">
            
            {/* Subtle physical texture overlay */}
            <div className="noise-overlay" />

            {/* Custom interactive mouse cursor */}
            <CustomCursor />

            {/* Sticky Global Top Navigation */}
            <Navbar theme={theme} toggleTheme={toggleTheme} />

            {/* Hero Landing Experience Area */}
            <Hero />

            {/* Story Bio Experience Timeline (#about) */}
            <ExperienceTimeline />

            {/* Curated Limited Logo Merchandise and Equipment (#store) */}
            <Store />

            {/* Skills Capabilities Accordion Section (#services) */}
            <Capabilities />

            {/* Selected Case Studies Portfolio Grid (#work) */}
            <WorkShowcase />

            {/* Clients Feedback recommendations slider */}
            <Testimonials />

            {/* Creative Collaboration contact Form (#contact) */}
            <ContactForm />

          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
